import { useState, type FC } from 'react'
import toast, { resolveValue, useToaster } from 'react-hot-toast'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import useTranslate from '@/hooks/use-translate'
import { getToastSeverity } from '@/utils/alertToastStyles'
import { TOAST_CONTENT_ATTRIBUTE, ToastCard } from './ToastCard'

/*
  Affiche les toasts de react-hot-toast en pile, en haut à droite : repliée, seules les trois dernières
  dépassent ; au survol, la pile se déplie et les durées se mettent en pause. Un balayage vers la droite ferme.
*/

const GAP = 8
const PEEK = 10
const MAX_PEEKING = 3
const SWIPE_DISTANCE = 80
const SWIPE_VELOCITY = 480

interface ToasterProps {
  /** Masque les toasts sans démonter : leur durée continue de s'écouler. */
  hidden?: boolean
}

export const Toaster: FC<ToasterProps> = ({ hidden = false }) => {
  const theme = useTheme()
  const { t: tNotifications } = useTranslate('notifications')
  const reduceMotion = useReducedMotion() ?? false
  const { toasts, handlers } = useToaster()
  const [expanded, setExpanded] = useState(false)
  const [swiped, setSwiped] = useState<ReadonlySet<string>>(new Set())

  // react-hot-toast range le plus récent en premier
  const visible = toasts.filter((t) => t.visible)
  const frontHeight = visible[0]?.height
  const offsets = visible.reduce<number[]>(
    (acc, t, index) => [...acc, acc[index] + (t.height ?? 0) + GAP],
    [0]
  )

  const spring = reduceMotion
    ? { duration: 0.15 }
    : { type: 'spring' as const, stiffness: 420, damping: 34, mass: 0.9 }

  return (
    <Box
      component="section"
      aria-label={tNotifications('title')}
      onMouseEnter={() => {
        setExpanded(true)
        handlers.startPause()
      }}
      onMouseLeave={() => {
        setExpanded(false)
        handlers.endPause()
      }}
      sx={{
        display: hidden ? 'none' : 'block',
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: theme.zIndex.snackbar,
        width: 'min(calc(100vw - 32px), 380px)',
        // la zone de survol suit la pile dépliée, pour ne pas la replier entre deux cartes
        height: expanded ? offsets[visible.length] : 0,
      }}
    >
      <AnimatePresence initial={false} custom={swiped}>
        {visible.map((t, index) => {
          const collapsedBehind = !expanded && index > 0
          const y = expanded ? offsets[index] : index * PEEK
          const peeking = expanded || index < MAX_PEEKING
          const message = resolveValue(t.message, t)

          return (
            <motion.div
              key={t.id}
              initial={
                reduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: -16, scale: 0.96 }
              }
              animate={{
                opacity: peeking ? 1 : 0,
                y,
                scale: collapsedBehind ? 1 - index * 0.05 : 1,
                height: collapsedBehind && frontHeight ? frontHeight : 'auto',
              }}
              variants={{
                // AnimatePresence passe les toasts balayés : ils sortent par la droite, les autres remontent
                exit: (swipedIds: ReadonlySet<string>) =>
                  swipedIds.has(t.id)
                    ? { opacity: 0, x: 420, transition: { duration: 0.2 } }
                    : {
                        opacity: 0,
                        y: reduceMotion ? y : y - 8,
                        transition: { duration: 0.2 },
                      },
              }}
              exit="exit"
              transition={spring}
              drag={!reduceMotion && (expanded || index === 0) ? 'x' : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={{ left: 0.1, right: 1 }}
              onDragEnd={(_, info) => {
                if (
                  info.offset.x > SWIPE_DISTANCE ||
                  info.velocity.x > SWIPE_VELOCITY
                ) {
                  setSwiped((prev) => new Set(prev).add(t.id))
                  toast.dismiss(t.id)
                }
              }}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100%',
                zIndex: visible.length - index,
                transformOrigin: '50% 0',
                overflow: collapsedBehind ? 'hidden' : 'visible',
                borderRadius: 10,
                pointerEvents: peeking ? 'auto' : 'none',
                touchAction: 'pan-y',
              }}
            >
              <Box
                ref={(el: HTMLDivElement | null) => {
                  // offsetHeight ignore l'échelle de la pile, contrairement à getBoundingClientRect
                  if (el && el.offsetHeight !== t.height) {
                    handlers.updateHeight(t.id, el.offsetHeight)
                  }
                }}
                {...t.ariaProps}
                sx={{
                  // derrière la première, une carte ne montre que son bord
                  [`& [${TOAST_CONTENT_ATTRIBUTE}]`]: {
                    opacity: collapsedBehind ? 0 : 1,
                    transition: 'opacity .15s',
                  },
                }}
              >
                {t.type === 'custom' ? (
                  message
                ) : (
                  <ToastCard
                    severity={getToastSeverity(t)}
                    message={message}
                    onClose={() => toast.dismiss(t.id)}
                  />
                )}
              </Box>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </Box>
  )
}
